/** Clock in / Change task / Clock out card. `compact` for the Staff Dashboard, full (with today's stretches) on Timesheets. */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Coffee, LogIn, LogOut, Loader2, MapPin, Play, RefreshCw, Users } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { formatApiError } from '@/utils/apiError';
import { fmtHours, textOn } from './utils';
import { TaskPrompt } from './TaskPrompt';
import { LateReasonForm } from './LateReasonForm';

const hhmm = (iso) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
const elapsed = (iso) => { const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000)); return `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`; };

async function api(path, method = 'GET', body) {
  const r = await fetch(`${API_URL}/api${path}`, { method, credentials: 'include', headers: body ? { 'Content-Type': 'application/json' } : undefined, body: body ? JSON.stringify(body) : undefined });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(formatApiError(d) || 'Request failed');
  return d;
}

const LOC_FAIL = {
  denied: "Your phone's location settings are off so mobile clocking in/out is unavailable — please either allow location sharing on your mobile or log in to your account on a company computer and clock in/out that way.",
  unavailable: "We couldn't get your phone's location so mobile clocking in/out is unavailable — turn Location Services on (or step outside) and try again, or log in on a company computer and clock in/out that way.",
  unsupported: "This browser can't share your location so mobile clocking in/out is unavailable — open the portal in Safari or Chrome on your phone, or log in on a company computer and clock in/out that way.",
};

const locate = () => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve({ location_status: 'unsupported' });
  navigator.geolocation.getCurrentPosition(
    (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy, location_status: 'ok' }),
    (err) => resolve({ denied: err?.code === 1, location_status: err?.code === 1 ? 'denied' : 'unavailable' }),
    { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 },
  );
});

export function ClockCard({ compact = false, onSaved }) {
  const navigate = useNavigate();
  const [s, setS] = useState(undefined);
  const { user } = useAuth();
  const isManager = ['admin', 'manager'].includes(user?.role);
  const [jobTypes, setJobTypes] = useState([]);
  const [prompt, setPrompt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [, tick] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const d = await api('/timesheets/clock');
      setS(d);
      if (d.auto_stopped) toast.info(`You were clocked out automatically at ${d.auto_stopped.stopped_at_label}`, { description: `${fmtHours(d.auto_stopped.minutes)} saved on ${d.auto_stopped.job_type_name}.`, duration: 10000 });
    } catch { setS(null); }
  }, []);
  useEffect(() => { refresh(); api('/timesheets/job-types').then(setJobTypes).catch(() => {}); }, [refresh]);
  useEffect(() => { if (s?.status !== 'in' && s?.status !== 'break') return undefined; const id = setInterval(() => tick((n) => n + 1), 1000); return () => clearInterval(id); }, [s?.status]);

  const clockIn = async () => {
    setBusy(true);
    try {
      const loc = await locate();
      if (loc.lat == null) { toast.error(LOC_FAIL[loc.location_status] || LOC_FAIL.denied, { duration: 12000 }); return; }
      setS(await api('/timesheets/clock/in', 'POST', { lat: loc.lat, lng: loc.lng, accuracy: loc.accuracy, location_status: loc.location_status }));
      window.dispatchEvent(new Event('clock-changed'));
      toast.success('Clocked in — have a good one!');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const submitTask = async (payload) => {
    try {
      const path = prompt === 'out' ? '/timesheets/clock/out' : prompt === 'break' ? '/timesheets/clock/break' : '/timesheets/clock/change-task';
      const loc = prompt === 'out' ? await locate() : {};
      if (prompt === 'out' && loc.lat == null) { toast.error(LOC_FAIL[loc.location_status] || LOC_FAIL.denied, { duration: 12000 }); return; }
      const d = await api(path, 'POST', { ...payload, lat: loc.lat, lng: loc.lng, accuracy: loc.accuracy, location_status: loc.location_status });
      setS(d); setPrompt(null); window.dispatchEvent(new Event('clock-changed'));
      if (payload.new_task_name) api('/timesheets/job-types').then(setJobTypes).catch(() => {});
      toast.success(prompt === 'out' ? `Clocked out — ${fmtHours(d.today_minutes)} logged today` : prompt === 'break' ? `${fmtHours(d.saved.minutes)} saved — enjoy your break` : `${fmtHours(d.saved.minutes)} saved on ${d.saved.job_type_name}`);
      onSaved?.(d.saved);
    } catch (e) { toast.error(e.message); }
  };

  if (s === undefined) return <div className={`rounded-xl border bg-white ${compact ? 'p-3' : 'p-4'} text-sm text-gray-400 flex items-center gap-2`} data-testid="clock-loading"><Loader2 className="w-4 h-4 animate-spin" /> Checking your clock…</div>;
  if (s === null || s.clock_required === false) return null;
  if (s.late_prompt) {
    return <LateReasonForm prompt={s.late_prompt} onSubmit={async (body) => { try { setS(await api('/timesheets/clock/late-reason', 'POST', body)); toast.success('Thanks — your manager has been told'); } catch (e) { toast.error(e.message); } }} />;
  }
  const t = s.timer;
  const current = t?.job_type_name;
  const onBreak = s.status === 'break';
  const resume = async () => { setBusy(true); try { setS(await api('/timesheets/clock/resume', 'POST', {})); window.dispatchEvent(new Event('clock-changed')); toast.success('Back to work — clock running'); } catch (e) { toast.error(e.message); } finally { setBusy(false); } };
  const clockOutFromBreak = async () => { setBusy(true); try { const loc = await locate(); if (loc.lat == null) { toast.error(LOC_FAIL[loc.location_status] || LOC_FAIL.denied, { duration: 12000 }); return; } const d = await api('/timesheets/clock/out', 'POST', { lat: loc.lat, lng: loc.lng, accuracy: loc.accuracy, location_status: loc.location_status }); setS(d); window.dispatchEvent(new Event('clock-changed')); toast.success(`Clocked out — ${fmtHours(d.today_minutes)} logged today`); onSaved?.(); } catch (e) { toast.error(e.message); } finally { setBusy(false); } };
  const breakChip = s.break_minutes ? `${fmtHours(s.break_minutes)} on break` : null;
  const Chip = ({ children, tid }) => <span className="inline-flex items-center rounded-full bg-white/80 border border-black/5 px-2.5 py-1 text-xs font-semibold text-gray-700" data-testid={tid}>{children}</span>;
  const Timer = () => <span className="font-mono text-lg sm:text-xl font-bold tabular-nums text-gray-900 shrink-0" data-testid="clock-elapsed">{elapsed(t.started_at)}</span>;
  const btn = 'h-11 w-full font-bold text-sm px-2';

  return (
    <div className={`rounded-xl border-2 ${onBreak ? 'border-amber-400 bg-amber-50' : s.status === 'in' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-white'} ${compact ? 'p-3' : 'p-4'}`} data-testid="clock-card" data-status={s.status}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${onBreak ? 'bg-amber-500 text-white' : s.status === 'in' ? 'bg-emerald-600 text-white' : 'bg-gray-900 text-white'}`}>{onBreak ? <Coffee className="w-5 h-5" /> : <Clock className="w-5 h-5" />}</div>
        <div className="flex-1 min-w-0">
          {onBreak ? (
            <>
              <div className="flex items-center justify-between gap-3">
                <div className="font-bold text-gray-900 text-base leading-tight" data-testid="clock-status">On break since {hhmm(t.started_at)}</div>
                <Timer />
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Chip tid="clock-today-chip">{fmtHours(s.today_minutes)} logged today</Chip>
                {breakChip && <Chip>{breakChip}</Chip>}
                {t.break_planned_minutes && <Chip tid="clock-break-plan">Back by {hhmm(new Date(new Date(t.started_at).getTime() + t.break_planned_minutes * 60000).toISOString())} · {t.break_planned_minutes} min</Chip>}
              </div>
              <div className="text-xs text-gray-600 mt-2" data-testid="clock-current-task">Clock paused — break time isn't counted.</div>
            </>
          ) : s.status === 'in' ? (
            <>
              <div className="flex items-center justify-between gap-3">
                <div className="font-bold text-gray-900 text-base leading-tight" data-testid="clock-status">Clocked in since {hhmm(s.clock_in_at)}</div>
                <Timer />
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Chip tid="clock-today-chip">{fmtHours(s.today_minutes)} logged today</Chip>
                {breakChip && <Chip>{breakChip}</Chip>}
                {t.cutoff_label && <Chip tid="clock-cutoff-chip">Auto clock-out {t.cutoff_label}</Chip>}
              </div>
              <div className="text-sm text-gray-800 mt-2 truncate" data-testid="clock-current-task">
                <span className="text-gray-500">Task:</span> {current ? <b>{current}</b> : <span className="italic text-gray-500">not set yet — tap Change task</span>}
              </div>
            </>
          ) : (
            <>
              <div className="font-bold text-gray-900 text-base leading-tight" data-testid="clock-status">{s.day?.clock_out_at ? `Clocked out at ${hhmm(s.day.clock_out_at)}` : 'Not clocked in'}</div>
              {s.day?.clock_out_at && <div className="mt-2"><Chip tid="clock-today-chip">{fmtHours(s.today_minutes)} logged today</Chip></div>}
              <div className="text-xs text-gray-500 flex items-center gap-1 mt-1.5"><MapPin className="w-3 h-3" /> {s.geofence_required ? "Tap when you arrive — we check you're on site" : 'Tap when you arrive — your location is recorded'}</div>
            </>
          )}
        </div>
      </div>
      <div className={`mt-3 grid gap-2 ${onBreak ? 'grid-cols-2' : s.status === 'in' ? 'grid-cols-3' : 'grid-cols-1'} ${compact ? 'sm:flex sm:flex-wrap sm:items-center' : ''}`} data-testid="clock-actions">
        {onBreak ? (
          <>
            <Button onClick={resume} disabled={busy} className={`${btn} bg-emerald-600 hover:bg-emerald-700 text-white sm:w-auto`} data-testid="clock-resume"><Play className="w-4 h-4 mr-1.5" /> Back to work</Button>
            <Button onClick={clockOutFromBreak} disabled={busy} variant="outline" className={`${btn} border-red-300 text-red-700 bg-white sm:w-auto`} data-testid="clock-out"><LogOut className="w-4 h-4 mr-1.5" /> Clock out</Button>
          </>
        ) : s.status === 'in' ? (
          <>
            <Button onClick={() => setPrompt('break')} variant="outline" className={`${btn} border-amber-400 text-amber-800 bg-white sm:w-auto`} data-testid="clock-break"><Coffee className="w-4 h-4 mr-1 hidden sm:inline" /> Break</Button>
            <Button onClick={() => setPrompt('change')} className={`${btn} bg-gray-900 text-white sm:w-auto`} data-testid="clock-change-task"><RefreshCw className="w-4 h-4 mr-1 hidden sm:inline" /> Change task</Button>
            <Button onClick={() => setPrompt('out')} variant="outline" className={`${btn} border-red-300 text-red-700 bg-white sm:w-auto`} data-testid="clock-out"><LogOut className="w-4 h-4 mr-1 hidden sm:inline" /> Clock out</Button>
          </>
        ) : (
          <Button onClick={clockIn} disabled={busy} className={`${btn} bg-emerald-600 hover:bg-emerald-700 text-white px-5 sm:w-auto`} data-testid="clock-in">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><LogIn className="w-4 h-4 mr-1.5" /> Clock in</>}</Button>
        )}
        {compact && <div className="col-span-full sm:col-span-1 sm:w-auto sm:ml-auto flex flex-wrap gap-1 justify-end">
          {isManager && <Button variant="ghost" onClick={() => navigate('/staff/attendance')} className="text-sky-800 font-semibold" data-testid="clock-open-attendance"><Users className="w-4 h-4 mr-1" /> Staff attendance →</Button>}
          <Button variant="ghost" onClick={() => navigate('/staff/timesheets')} className="text-gray-600" data-testid="clock-open-timesheets">Open timesheets →</Button>
        </div>}
      </div>
      {!compact && s.segments?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="clock-segments">
          {s.segments.map((e) => <span key={e.id} className="text-xs font-semibold rounded-full px-2 py-1" style={{ background: e.job_type_colour || '#475569', color: textOn(e.job_type_colour || '#475569') }} title={e.description}>{e.start}–{e.end} {e.job_type_name} · {fmtHours(e.minutes)}</span>)}
        </div>
      )}
      <TaskPrompt open={!!prompt} mode={prompt} timer={t} jobTypes={jobTypes} onSubmit={submitTask} onClose={() => setPrompt(null)} />
    </div>
  );
}
